import React from 'react';
import Sparkline from 'core/chart/sparkline';
import { SemanticUI, SVG } from '@iterable/deprecated_react_components';
import { abbrNumber } from 'utils/chart';
import { getTheme, numberRangeThemes } from 'utils/themes';
import tinycolor from 'tinycolor2';
import { determineTrendColor, determineRangeColor } from './utils';

import {
  NumberComponentContainer,
  PopupContentContainer,
  NumberDisplayContainer,
} from './Number.styled';

// type Props = {
//   /** An additional class. */
//   className: ?string,
//   /** Number of decimal places for shortened/abbreviated numbers. */
//   decimalPlaces: number,
//   /** Series data to show the number sparkline. */
//   data: [],
//   /**
//    * Additional options for number.
//    * {
//    *  { Boolean } customTrend: If custom trend colors are used.
//    *  { Boolean } enableRange: If ranges are used to color number.
//    *  { Boolean } isPercent: Whether of not the displayed number is a percent.
//    *  { Boolean } invertColors: Trend color is on text or background.
//    *  { Object } rangeTheme: the rangeTheme containing the min & max values and theme name.
//    *  { Array } rangeThresholds: Array of range thresholds [{ value, color }, ...]
//    *  { Boolean } showSparkline: Display sparkline.
//    *  { Boolean } showTrend: Display trend.
//    *  { Object } trendColors: Object containing trend up and down colors { up, down }.
//    *  { String } trendDirection: Direction of number trend.
//    * }
//    */
//   options: {
//     customTrend: boolean,
//     enableRange: boolean,
//     isPercent: boolean,
//     invertColors: boolean,
//     rangeThresholds: [],
//     rangeTheme: {},
//     showSparkline: ?boolean,
//     showTrend: ?boolean,
//     trendColors: {},
//     trendDirection: ?string,
//   },
//   /** Previous Value of number. */
//   previousValue: number,
//   /** Current Value of number. */
//   currentValue: number,
//   /* Theme of the number chart. */
//   theme: string,
//   /** Title of the number chart. */
//   label: ?string,
// };

const MAX_DISPLAY_VALUE = 9999;

export default class NumberComponent extends React.Component {
  static defaultProps = {
    theme: 'default',
    decimalPlaces: 1,
  };

  determineTrendColor = determineTrendColor;
  determineRangeColor = determineRangeColor;

  /**
   * getTrendStyles
   * Generates a style definition.
   * @param { Boolean } trendUp: If the trand direction is positive.
   * @param { Number } value: The number value.
   * @param { Object } options: options of the number.
   * @return { Object } Theme object definition.
   */
  getTrendStyles = (trendUp, value, options) => {
    const { currentValue, previousValue, theme } = this.props;
    const { customTrend, enableRange, rangeTheme, rangeThresholds, trendColors } = options;
    const { up: upColor, down: downColor } = trendColors || {};
    const themeDef = getTheme(theme);
    let styles = {};

    if (currentValue > previousValue) {
      styles = {
        iconName: 'chevron up',
        invertBackgroundColor: this.determineTrendColor(
          trendUp,
          themeDef.numberInvertPositiveBackgroundColor,
          themeDef.numberInvertNegativeBackgroundColor,
          customTrend,
          upColor,
        ),
        numberColor: this.determineTrendColor(
          trendUp,
          themeDef.numberPositiveColor,
          themeDef.numberNegativeColor,
          customTrend,
          upColor,
        ),
      };
    } else if (previousValue > currentValue) {
      styles = {
        iconName: 'chevron down',
        invertBackgroundColor: this.determineTrendColor(
          trendUp,
          themeDef.numberInvertNegativeBackgroundColor,
          themeDef.numberInvertPositiveBackgroundColor,
          customTrend,
          downColor,
        ),
        numberColor: this.determineTrendColor(
          trendUp,
          themeDef.numberNegativeColor,
          themeDef.numberPositiveColor,
          customTrend,
          downColor,
        ),
      };
    } else {
      styles = {
        iconName: 'chevron right',
        invertBackgroundColor: themeDef.numberNeutralColor,
        numberColor: themeDef.numberInvertTextColor,
      };
    }
    if (enableRange) {
      const rangeStyles = this.determineRangeColor(value, rangeTheme, rangeThresholds);
      return { ...styles, ...rangeStyles };
    }
    return styles;
  };

  /**
   * getDisplayValues
   * Calculates the values to display.
   * @param { Number } currentValue: The current value.
   * @param { Number } previousValue: The previous value.
   * @param { Boolean } isPercent: If the value is a percent.
   * @returns { Object } Object containing displayValue, differenceValue and precentChange.
   */
  getDisplayValues = (currentValue, previousValue, isPercent) => {
    const { decimalPlaces } = this.props;
    const appendPercent = isPercent ? '%' : '';
    const baseCurrentValue = isPercent ? currentValue * 100 : currentValue;
    const basePreviousValue = isPercent ? previousValue * 100 : previousValue;

    const difference = baseCurrentValue - basePreviousValue;
    const differenceValue =
      Math.abs(difference) > MAX_DISPLAY_VALUE
        ? abbrNumber(difference, decimalPlaces)
        : difference.toLocaleString(undefined, { maximumFractionDigits: decimalPlaces });
    const percentChange = basePreviousValue === 0 ? 1 : difference / basePreviousValue;

    let displayValue = '';
    if (baseCurrentValue > MAX_DISPLAY_VALUE) {
      displayValue = abbrNumber(baseCurrentValue, decimalPlaces);
    } else {
      displayValue =
        baseCurrentValue || baseCurrentValue === 0
          ? baseCurrentValue.toLocaleString(undefined, { maximumFractionDigits: decimalPlaces })
          : '';
    }

    return {
      value: baseCurrentValue,
      displayValue: `${displayValue}${appendPercent}`,
      differenceValue: `${differenceValue}${appendPercent}`,
      differenceValueFull: `${difference.toLocaleString()}${appendPercent}`,
      percentChange: `${parseFloat(percentChange * 100).toFixed(decimalPlaces)}%`,
    };
  };

  render() {
    const { className, currentValue, data, previousValue, options, label, theme } = this.props;
    const {
      customTrend,
      enableRange,
      isPercent,
      invertColors,
      rangeTheme,
      rangeThresholds,
      showSparkline,
      showTrend,
      trendColors,
      trendDirection,
    } = options || {
      customTrend: false,
      enableRange: false,
      isPercent: false,
      invertColors: false,
      rangeTheme: {
        customTheme: 0,
        reverse: false,
        themeName: numberRangeThemes.default.name,
        min: 0,
        max: 100,
      },
      rangeThresholds: [],
      showSparkline: true,
      showTrend: true,
      trendColors: { up: '#00d034', down: '#eb144c' },
      trendDirection: 'up',
    };

    const { value, displayValue, differenceValue, differenceValueFull, percentChange } =
      this.getDisplayValues(currentValue, previousValue, isPercent);

    const themeDef = getTheme(theme);
    const trendUp = trendDirection === 'up';

    const { iconName, invertBackgroundColor, numberColor } = this.getTrendStyles(trendUp, value, {
      customTrend,
      enableRange,
      rangeTheme,
      rangeThresholds,
      trendColors,
    });
    const invertBackground =
      showTrend || enableRange ? invertBackgroundColor : themeDef.numberNeutralColor;

    const backgroundStyles = {
      backgroundColor: invertColors ? invertBackground : themeDef.numberBackgroundColor,
    };
    const labelStyles = {
      color: invertColors ? themeDef.numberInvertTextColor : numberColor,
    };
    const numberStyles =
      enableRange && !invertColors
        ? {
            color: numberColor,
            fill: numberColor,
          }
        : {
            color: invertColors ? themeDef.numberInvertTextColor : themeDef.numberTextColor,
            fill: invertColors ? themeDef.numberInvertTextColor : themeDef.numberTextColor,
          };
    const trendColor = tinycolor(backgroundStyles.backgroundColor);

    const numberDisplay = (
      <NumberDisplayContainer style={numberStyles}>
        <div className="cssNumberComponentDisplayValueHidden">{displayValue}</div>
        <SVG
          className="cssNumberComponentDisplayValue"
          height="100%"
          width="100%"
          version="1.1"
          viewBox="-40 -7 80 14"
          preserveAspectRatio="xMidYMin meet"
          style={{ fontFamily: 'proxima-nova' }}
        >
          <text fontSize="16" dy=".3em">
            {displayValue}
          </text>
        </SVG>
      </NumberDisplayContainer>
    );

    const numberPopup = (
      <SemanticUI.Popup
        trigger={numberDisplay}
        content={
          <PopupContentContainer>
            <div>{currentValue && currentValue.toLocaleString()}</div>
            <div>{label}</div>
            {showTrend && (
              <div>
                <SemanticUI.Icon name={iconName} />
                {differenceValueFull} ({percentChange})
              </div>
            )}
          </PopupContentContainer>
        }
        inverted
        on="hover"
        position="top center"
      />
    );

    return (
      <NumberComponentContainer
        className={className}
        data-custom-theme={customTrend || enableRange}
        data-invert={!!invertColors}
        data-number-dark={trendColor.isDark()}
        data-theme={theme}
        data-type="number"
        showSparkline={showSparkline}
        style={backgroundStyles}
      >
        <div className="cssNumberComponentValueLabelContainer">
          {currentValue > 1 ? numberPopup : numberDisplay}
          <div className="cssNumberComponentLabelContainer">
            {label && (
              <div className="cssNumberComponentLabel" style={numberStyles}>
                {label}
              </div>
            )}
            {showTrend && (
              <div className="cssNumberComponentTrendLabel" style={labelStyles}>
                <SemanticUI.Icon name={iconName} />
                {differenceValue} ({percentChange})
              </div>
            )}
          </div>
        </div>
        {showSparkline && !isPercent && (
          <div className="cssNumberComponentSparklineContainer">
            <Sparkline
              data={{ data }}
              sparklineColor={invertColors ? getTheme(theme).numberInvertTextColor : undefined}
              theme={theme}
            />
          </div>
        )}
      </NumberComponentContainer>
    );
  }
}
